"use client"

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@t-one-internal/design-system/lib/components/ui/popover';
import { Button, Input, Spinner } from '@t-one-internal/design-system';
import { Search, ChevronRight, ChevronDown, Building2, Folder, Briefcase, Check } from 'lucide-react';
import apiClient from '../../api/apiClient';

interface ScopeInstance {
  scope_instance_id: string;
  name: string;
  parent_instance_id: string | null;
  level_name?: string;
}

interface OrgUnitSelectorProps {
  value: string | undefined | null;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
}

interface TreeNode extends ScopeInstance {
  children: TreeNode[];
  depth: number;
}

export const OrgUnitSelector: React.FC<OrgUnitSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Select organizational unit',
  disabled = false,
  allowClear = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [instances, setInstances] = useState<ScopeInstance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasInitialized = useRef(false);

  const fetchInstances = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await apiClient.get<ScopeInstance[]>('/admin/scope-instances');
      const sorted = (data || []).sort((a, b) => a.name.localeCompare(b.name));
      setInstances(sorted);
    } catch (error) {
      console.error('Failed to fetch scope instances:', error);
      setInstances([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  // Build tree
  const tree = useMemo(() => {
    const buildTree = (parentId: string | null, depth: number): TreeNode[] => {
      return instances
        .filter((inst) => {
          if (parentId === null) return !inst.parent_instance_id;
          return inst.parent_instance_id === parentId;
        })
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((inst) => ({
          ...inst,
          depth,
          children: buildTree(inst.scope_instance_id, depth + 1),
        }));
    };
    return buildTree(null, 0);
  }, [instances]);

  // Expand all on first load
  useEffect(() => {
    if (instances.length > 0 && !hasInitialized.current) {
      setExpandedIds(new Set(instances.map((i) => i.scope_instance_id)));
      hasInitialized.current = true;
    }
  }, [instances]);

  // Filter tree
  const filteredTree = useMemo(() => {
    if (!searchQuery.trim()) return tree;
    const lowerQuery = searchQuery.toLowerCase();

    const filterNodes = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.reduce<TreeNode[]>((acc, node) => {
        const matchesSelf = node.name.toLowerCase().includes(lowerQuery);
        const filteredChildren = filterNodes(node.children);
        if (matchesSelf || filteredChildren.length > 0) {
          acc.push({ ...node, children: filteredChildren });
        }
        return acc;
      }, []);
    };

    return filterNodes(tree);
  }, [tree, searchQuery]);

  const toggleExpand = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelect = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
  };

  const selectedLabel = useMemo(() => {
    return instances.find((i) => String(i.scope_instance_id) === String(value))?.name;
  }, [instances, value]);

  const cn = (...classes: (string | boolean | undefined)[]) => classes.filter(Boolean).join(' ');

  const renderTreeNodes = (nodes: TreeNode[]) => {
    if (nodes.length === 0) return null;

    return (
      <ul className="space-y-0.5">
        {nodes.map((node) => {
          const isExpanded = expandedIds.has(node.scope_instance_id);
          const hasChildren = node.children.length > 0;
          const isSelected = String(value) === String(node.scope_instance_id);

          let NodeIcon = Folder;
          if (node.depth === 0) NodeIcon = Building2;
          else if (!hasChildren) NodeIcon = Briefcase;

          return (
            <li key={node.scope_instance_id}>
              <div
                className={cn(
                  'flex items-center gap-1.5 py-1.5 px-2 rounded-md cursor-pointer text-sm group transition-colors',
                  isSelected ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-slate-50 text-slate-700',
                )}
                style={{ paddingLeft: `${node.depth * 10 + 8}px` }}
                onClick={() => handleSelect(node.scope_instance_id)}
              >
                <button
                  type="button"
                  onClick={(e) => hasChildren && toggleExpand(e, node.scope_instance_id)}
                  className={cn(
                    'p-0.5 rounded-sm hover:bg-slate-200 text-slate-400 transition-colors',
                    !hasChildren && 'invisible',
                  )}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                  )}
                </button>
                <NodeIcon
                  className={cn(
                    'w-4 h-4',
                    isSelected ? 'text-indigo-500' : 'text-slate-400 group-hover:text-slate-500',
                  )}
                />
                <span className="flex-1 truncate select-none">
                  {node.name}
                  {node.level_name && (
                    <span className="ml-1.5 text-[0.65rem] uppercase tracking-wider text-slate-400 bg-slate-100 px-1 py-0.5 rounded-sm font-medium border border-slate-200">
                      {node.level_name}
                    </span>
                  )}
                </span>
                {isSelected && <Check className="w-3.5 h-3.5 text-indigo-600" />}
              </div>
              {isExpanded && hasChildren && (
                <div className="border-l border-slate-100 ml-[15px]">
                  {renderTreeNodes(node.children)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between h-9 px-3 text-sm bg-white font-normal hover:bg-slate-50',
            !value && 'text-slate-500',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
          disabled={disabled}
        >
          <span className="truncate">
            {isLoading ? 'Loading...' : selectedLabel || placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="p-0 w-[400px]" align="start">
        <div className="flex flex-col max-h-[400px]">
          <div className="p-2 border-b border-slate-100 sticky top-0 bg-white z-10 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400 ml-1" />
            <Input
              value={searchQuery}
              onChange={(e: any) => setSearchQuery(e.target.value)}
              placeholder="Search units..."
              className="border-none focus-visible:ring-0 px-2 h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="flex-1 overflow-y-auto p-1.5 min-h-[100px]">
            {allowClear && !searchQuery && (
              <div
                className={cn(
                  'flex items-center gap-2 py-2 px-3 rounded-md cursor-pointer text-sm transition-colors mb-1 border-b border-slate-100 pb-2',
                  !value ? 'bg-slate-50 text-slate-700' : 'hover:bg-slate-50 text-slate-600',
                )}
                onClick={handleClear}
              >
                <Building2 className="w-4 h-4 text-slate-400" />
                <span className="flex-1">Not Assigned</span>
                {!value && <Check className="w-3.5 h-3.5 text-indigo-600" />}
              </div>
            )}
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner size="sm" />
              </div>
            ) : filteredTree.length > 0 ? (
              renderTreeNodes(filteredTree)
            ) : (
              <div className="py-8 text-center text-sm text-slate-500">
                No organizational units found.
              </div>
            )}
          </div>
          <div className="p-1 border-t border-slate-100 bg-slate-50 flex justify-between px-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[10px] text-slate-500 px-2"
              onClick={() => setExpandedIds(new Set(instances.map((i) => i.scope_instance_id)))}
            >
              Expand All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-[10px] text-slate-500 px-2"
              onClick={() => setExpandedIds(new Set())}
            >
              Collapse All
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
